import { Box } from '@material-ui/core';
import { useState } from 'react';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { KycIntegrationMethodOwner } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { StandardActionDialog } from '@/components/shared/components/StandardActionDialog/StandardActionDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export const KYCAction = ({
  title,
  owner,
  description,
  emailVerification,
  manualVerification,
}: {
  title: string;
  owner: KycIntegrationMethodOwner;
  description: (string | { text: string; variables: string[] })[];
  emailVerification?: {
    buttonText: string;
    onSubmit: () => void;
    isLoading: boolean;
    isError: boolean;
  };
  manualVerification?: {
    allowed: boolean;
    onSubmit: () => void;
    onCancel: () => void;
    isLoading: boolean;
    isError: boolean;
  };
}) => {
  const { getTextIdTranslation } = useTranslation();
  const { ref, width } = useWidth<HTMLDivElement>();
  const { palette } = useCustomTheme();

  const isSmall = width && width < 500;

  const [open, setOpen] = useState(false);

  const openVerifyPressenciallyModal = () => setOpen(true);

  const closeVerifyPressenciallyModal = (internalModalState: boolean) => {
    if (internalModalState === false) {
      setOpen(false);
      manualVerification?.onCancel();
    }
  };

  const ownerLabel = getTextIdTranslation(`KYC_OWNER_LABEL_${owner}`);

  return (
    <>
      <CustomPaper fill="paper">
        <div ref={ref}>
          <Box
            gridGap={24}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={isSmall ? 'column' : 'row'}
          >
            <FlexColumn gridGap={4}>
              <FlexRow gridGap={6}>
                <Font variant="subtitle1" color="textPrimary" display="block">
                  <Text strong tId={title} /> {'('}
                  <Text tId={owner === KycIntegrationMethodOwner.BORROWER ? 'KYC_TITULAR' : 'KYC_COTITULAR'} />
                  {')'}
                </Font>
              </FlexRow>
              <Font variant="body2" color="textPrimary">
                {description.map((data, i) => {
                  const paragraph = typeof data === 'string' ? data : data.text;
                  const variables = typeof data === 'string' ? [] : data.variables;

                  return (
                    <Box mt={'4px'}>
                      <Text tId={paragraph} key={i} dynamicDataTexts={variables} />
                    </Box>
                  );
                })}
              </Font>

              <FlexRow pt={1} gridGap={10} flexWrap="wrap">
                {manualVerification?.allowed && (
                  <Box flexShrink={0}>
                    <StandardButton size="small" variant="outlined" onClick={openVerifyPressenciallyModal}>
                      <Text tId="KYC_STEP_PRESENTIAL_VERIFICATION_TEXT" />
                    </StandardButton>
                  </Box>
                )}

                {emailVerification && (
                  <Box flexShrink={0}>
                    <StandardButton
                      main
                      size="small"
                      onClick={emailVerification.onSubmit}
                      loading={emailVerification.isLoading}
                    >
                      <Text tId={emailVerification.buttonText} />
                    </StandardButton>
                  </Box>
                )}
              </FlexRow>
              {manualVerification?.allowed && (
                <Box>
                  <Font variant="caption" color="textSecondary">
                    *
                    <Text tId="KYC_STEP_DISCLAIMER" dynamicDataTexts={[ownerLabel]} />
                  </Font>
                </Box>
              )}
              {emailVerification?.isError && (
                <FlexRow gridGap={4}>
                  <Icon size={18} color={palette.error.main}>
                    error
                  </Icon>
                  <Font variant="body2" color="error">
                    <Text tId="KYC_ERROR_SENDING_VERIFICATION_EMAIL" />
                  </Font>
                </FlexRow>
              )}
            </FlexColumn>
          </Box>
        </div>
      </CustomPaper>
      <StandardActionDialog
        opened={open}
        setOpened={closeVerifyPressenciallyModal}
        titleTextElement={
          <>
            <Text tId="KYC_STEP_PRESENTIAL_VERIFICATION_TEXT" />
            <span style={{ fontWeight: 400 }}>
              {' ('}
              <Text tId={owner === KycIntegrationMethodOwner.BORROWER ? 'KYC_TITULAR' : 'KYC_COTITULAR'} />
              {')'}
            </span>
          </>
        }
        setActionDialogParams={() => {}}
        actionButtonsArray={[
          {
            onClick: manualVerification?.onSubmit || (() => {}),
            buttonLabelTextId: 'KYC_STEP_PRESENTIAL_VERIFICATION_BUTTON_TEXT',
          },
        ]}
        loading={manualVerification?.isLoading}
      >
        <FlexColumn>
          <Text tId="KYC_STEP_PRESENTIAL_VERIFICATION_MODAL_TEXT_1" dynamicDataTexts={[ownerLabel]} />
          <Text tId="KYC_STEP_PRESENTIAL_VERIFICATION_MODAL_TEXT_2" />
        </FlexColumn>
        {manualVerification?.isError && (
          <FlexRow alignItems="center" justifyContent="flex-start" mt="16px">
            <Icon size={20} color={palette.error.main}>
              error
            </Icon>
            <Font variant="body1" color="error">
              <Text tId="KYC_ERROR_PERFORMING_MANUAL_VERIFICATION" />
            </Font>
          </FlexRow>
        )}
      </StandardActionDialog>
    </>
  );
};
